/**
 * Centralized Pendo event tracking.
 *
 * Sits alongside the GA4 `explorerAnalytics` system (src/lib/analytics.ts) — same
 * user actions, different destination. Pendo uses these events to power segments,
 * funnels, and Guide targeting that GA4 can't (GA4 is aggregate; Pendo is per-visitor).
 *
 * Safety: all calls are no-ops if window.pendo.track isn't loaded yet. Pendo's CDN
 * snippet may still be loading on first paint, and we don't want analytics to throw.
 */

import { config } from '@/config';

// ─────────────────────────────────────────────────────────────────────────────
// Event catalog — the 8 events defined in the Pendo instrumentation design spec.
// (docs/superpowers/specs/2026-04-09-pendo-full-instrumentation-design.md)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Metadata shapes for each Pendo event. Strongly typed so that callers get
 * autocomplete and compile-time errors if they forget a field.
 *
 * Design choices made here:
 *  - Enums (forward_mode, auth_action) use string literal unions for safety.
 *  - resource/endpoint are optional on sdk_language_switched because the
 *    language selector can appear in contexts without an endpoint (e.g., homepage).
 *  - error_message truncation happens at the call site (matches GA4 pattern in
 *    explorerAnalytics.apiError — one less place to remember the rule).
 */
export type PendoEventMetadata = {
  // ─── API Playground ─────────────────────────────────────────────────────
  api_request_executed: {
    resource: string;
    endpoint: string;
    http_method: string;
    sdk_language: string;
    is_logged_in: boolean;
  };

  api_request_failed: {
    resource: string;
    endpoint: string;
    http_method: string;
    error_type: string;
    error_message: string; // truncate to 200 chars at call site
    http_status?: number;
  };

  code_sample_copied: {
    resource: string;
    endpoint: string;
    sdk_language: string;
    is_logged_in: boolean;
  };

  sdk_language_switched: {
    from_language: string;
    to_language: string;
    resource?: string;
    endpoint?: string;
  };

  auth_action: {
    action: 'clicked' | 'completed' | 'failed';
    source: string;
    resource?: string;
    endpoint?: string;
  };

  // ─── Event Explorer ─────────────────────────────────────────────────────
  event_forwarded: {
    event_type: string;
    forward_mode: 'proxy' | 'direct';
    is_success: boolean;
    status_code?: number;
    duration_ms?: number;
  };

  event_auto_forward_toggled: {
    enabled: boolean;
    forward_mode?: 'proxy' | 'direct';
  };

  event_forward_configured: {
    forward_mode: 'proxy' | 'direct';
  };
};

export type PendoEventName = keyof PendoEventMetadata;

/**
 * Fire a Pendo track event. Silently no-ops if Pendo isn't loaded.
 *
 * @example
 *   pendoTrack('api_request_executed', {
 *     resource: 'subscription',
 *     endpoint: 'create_a_subscription',
 *     http_method: 'POST',
 *     sdk_language: 'python',
 *     is_logged_in: true,
 *   });
 */
export function pendoTrack<E extends PendoEventName>(
  event: E,
  metadata: PendoEventMetadata[E]
): void {
  try {
    if (typeof window === 'undefined') return;
    if (!window.pendo?.track) {
      // Pendo not loaded yet (either still initializing, or disabled in this env).
      // Log in dev so devs see events firing locally.
      if (config.appEnv !== 'production') {
        console.log('[Pendo:not-loaded]', event, metadata);
      }
      return;
    }
    window.pendo.track(event, metadata as Record<string, unknown>);
    if (config.appEnv !== 'production') {
      console.log('[Pendo]', event, metadata);
    }
  } catch {
    // Never throw from analytics.
  }
}
